<template>
  <div
    class="app-container chatbot-container bg-teal text-white"
    :class="{'intro flex-column justify-content-center align-items-center' : inIntro}">
    <template v-if="!inIntro || !drawerMinimized">
      <FadeTransition
        :key="inIntro ? 'intro' : 'conversation'"
        group>
        <div
          v-if="inIntro && !drawerMinimized"
          :key="'intro'"
          class="d-flex flex-column justify-content-center align-items-center flex-grow-1 px-lg pb-0 h-100">
          <div class="d-flex flex-column justify-content-center align-items-center flex-grow-1">
            <ConversationalSearchIcon class="mb-md"/>
            <h3>{{ greetingMessage }}</h3>
            <p class="mb-sm">
              {{ greetingQuestion }}
            </p>
            <FadeTransition>
              <div
                v-if="suggestedQueries.length > 0"
                class="d-flex flex-column align-items-center">
                <div class="suggested-queries d-flex flex-wrap justify-content-center gap-lg mb-sm">
                  <StylistButton
                    v-for="query in suggestedQueries"
                    :key="query"
                    :text="query.query"
                    variant="light"
                    class="condensed"
                    @click="handleSuggestionClick(query.query)"/>
                </div>
                <p class="text-white text-center">
                  or type your message below
                </p>
              </div>
            </FadeTransition>
          </div>
          <BaseButton
            v-if="messageHistory.length > 0"
            text="View previous messages"
            variant="text-link-light"
            class="mx-auto mt-auto"
            @click="inIntro = false"/>
        </div>
      </FadeTransition>

      <div
        v-if="!inIntro && !drawerMinimized"
        ref="conversationContainer"
        key="conversationContainer"
        class="conversation-container d-flex flex-column bg-white pb-xxl">
        <div
          v-if="fetchHistoryErrorMessage"
          class="m-md align-self-center border bg-origami mx-md">
          <p class="fine-print text-orchid text-center  my-xs mx-md">
            {{ fetchHistoryErrorMessage }}
          </p>
        </div>
        <div
          v-if="fetchingMessageHistory"
          class="d-flex justify-content-center">
          <SvgSpinner
            class="my-lg"
            width="32"
            height="32"/>
        </div>

        <StylistButton
          v-else
          text="View previous messages"
          variant="text-link"
          class="align-self-center"
          @click="handleGetPreviousMessages()"/>
        <hr class="mt-0 mx-md">
        <div class="d-flex flex-column gap-md">
          <MessageGroup
            v-for="message in messageHistory"
            :key="message.id"
            :message="message"
            :data-index="message.id"
            @send-message="handleSuggestionClick"/>
        </div>
      </div>
    </template>
    <div
      v-if="!drawerMinimized"
      class="w-100 p-lg d-flex align-items-center flex-column">
      <ChatbotTextArea
        v-model="data.newMessage"
        :disabled="disableTextArea ? true : null"
        class="w-100"
        label="Send a message"
        placeholder="Send a message"
        @send-message="sendNewMessage(data.newMessage)"/>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, nextTick, useTemplateRef } from 'vue'
import { debouncedWatch } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useElementSize } from '@/composables/element.js'
import { useOverlaysStore } from '@/stores/overlays.js'
import { useStylingChatbotStore } from '@/stores/stylingChatbot.js'
import BaseButton from '@shared/components/ADORN/BaseButton'
import StylistButton from '@shared/components/ADORN/StylistButton.vue'
import ChatbotTextArea from '@/components/chatbot/ChatbotTextArea.vue'
import FadeTransition from '@shared/components/ADORN/transitions/FadeTransition.vue'
import MessageGroup from './MessageGroup.vue'
import ConversationalSearchIcon from '@shared/components/icons/ConversationalSearchIcon.vue'
import SvgSpinner from '@/components/global/svg/SvgSpinner.vue'

const overlaysStore = useOverlaysStore()
const stylingChatbotStore = useStylingChatbotStore()

const { postNewMessage, fetchSuggestedQueries, fetchMessageHistory } = stylingChatbotStore
const { drawerMinimized } = storeToRefs(overlaysStore)
const {
  greetingMessage,
  greetingQuestion,
  inIntro,
  messageHistory,
  suggestedQueries,
  fetchingMessageHistory,
  disableTextArea,
  fetchHistoryErrorMessage,
  allowScroll
} = storeToRefs(stylingChatbotStore)

const data = ref({
  newMessage: '',
})

const conversationContainer = useTemplateRef('conversationContainer') 
async function sendNewMessage(message) {
  inIntro.value = false
  postNewMessage(message)
  data.value.newMessage = ''
  allowScroll.value = true
  scrollToBottom()
}

function handleSuggestionClick(query) {
  const notCapitalizedQuery = query.charAt(0).toLowerCase() + query.slice(1)
  const prependedQuery = `I'm looking for ${notCapitalizedQuery}`
  sendNewMessage(prependedQuery)
}

async function handleGetPreviousMessages() {
  allowScroll.value = false
  await fetchMessageHistory()
}

function scrollToBottom() {
  const container = conversationContainer.value
  if (!container) return
  container.scrollTo({
    top: container.scrollHeight,
    behavior: 'smooth'
  })
}

const conversationContainerSize = useElementSize(conversationContainer)
const conversationContainerHeight = computed(() => {
  return conversationContainerSize.value.scrollHeight
})

debouncedWatch(conversationContainerHeight, async () => {
  const container = conversationContainer.value
  const minimized = drawerMinimized.value
  await nextTick()
  if (!container) return
  if (!minimized && allowScroll.value) {
    scrollToBottom()
  }
}, {
  debounce: 250,
  immediate: true
})



onMounted(() => {
  fetchSuggestedQueries()
  fetchMessageHistory()
})
</script>

<style scoped lang="scss">
.chatbot-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0; 
}
.conversation-container {
  overflow-x: hidden;
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0; 
}
.chatbot-container.intro {
  display: inline-flex;
}
.conversation-container {
  transform-origin: bottom;
  animation: growFromBottom 0.5s ease-in forwards;
}

@keyframes growFromBottom {
  from {
    transform: scaleY(0);
    overflow-y: hidden;
  }
  to {
    transform: scaleY(1);
    overflow-y: auto;
  }
}
.suggested-queries {
  max-width: 350px;
}
</style>
